// ───────────────────────────────────────────────────────────────────────────
// MonthsList — the month selection grid (the year-level analogue of `Month`).
//
// Mirrors `@mantine/dates`' `MonthsList` API (props, defaults, `__`-prefixed
// internal hooks) but is built on `@knitui/components` (`Box`) + `tamagui`
// (`styled`/`createStyledContext`/slot sugar) + `@knitui/dates` `PickerControl`
// + dayjs — NEVER HTML `<table>`/`<tr>`/`<td>`, so it renders on web AND native
// from one source. Accent comes from the active Tamagui theme (`theme="red"`),
// never a Mantine-style `color` prop.
//
// This is a GRID component: per-cell sizing/colour/interaction (checklist #3/#4/
// #12) live in the `PickerControl` leaf — MonthsList just lays out the 4×3 rows/
// cells and shares `size` down via context. Selected/disabled/in-range state and
// each control's roving `tabIndex` (`getMonthInTabOrder`) are computed here and
// handed to each `PickerControl` as boolean props (which it expresses as
// variants), so nothing in MonthsList uses a dynamic `opacity`/`display` style
// prop (checklist #15): `fullWidth` is a boolean VARIANT on the frame/row/cell,
// not a per-render spread the optimiser could fold onto a whole cell.
// ───────────────────────────────────────────────────────────────────────────
import { memo, useMemo } from 'react';
import { createStyledContext, type GetProps, styled, withStaticProperties } from 'tamagui';

import {
  PickerControl,
  PickerControlProps
} from '@unistocks/components/calendar/picker-control/picker-control';
import {
  ControlKeyboardEvent,
  ControlPressEvent,
  ControlsGroupSettings,
  DateStringValue
} from '@unistocks/components/calendar/types';
import { areCellPropsEqual } from '@unistocks/components/calendar/utils/are-cell-props-equal';
import { focusElement } from '@unistocks/components/calendar/utils/focus-element';
import { hasPreventDefault } from '@unistocks/components/calendar/utils/has-prevent-default';
import { memoizeByDate } from '@unistocks/components/calendar/utils/memoize-control-props';
import { SlotStyles, slotStyles } from '@unistocks/components/calendar/utils/slot-styles';
import { useDatesContext } from '@unistocks/components/date-picker/date-picker-context';
import { useCallbackRef } from '@unistocks/components/date-picker/hooks/use-callback-ref';
import { withFeedback } from '@unistocks/shared/utils/haptics/with-feedback';
import { createDate } from '@unistocks/shared/utils/util';
import { View } from 'tamagui';
import { type CalendarSize, CELL_SPACING } from '../cell-metrics';
import { getMonthInTabOrder } from './get-month-in-tab-order/get-month-in-tab-order';
import { getMonthsData } from './get-months-data/get-months-data';
import { isMonthDisabled } from './is-month-disabled/is-month-disabled';

/**
 * Cross-platform press-in event for a control, derived from `PickerControl`'s own
 * `onPressIn` prop signature — never a DOM `React.MouseEvent`. Used to port
 * Mantine's `preventFocus` (mousedown → preventDefault) guard.
 */
type ControlPressInEvent = Parameters<NonNullable<PickerControlProps['onPressIn']>>[0];

// ── 2. Shared context ───────────────────────────────────────────────────────
// One `createStyledContext` carries `size` to the styled parts (frame/row/cell)
// so they share the SAME size the consumer set on the root without prop-drilling
// — the same mechanism `Month`/`PickerControl` use. The control cells themselves
// still take an explicit `size` prop (they own the `size` variant).
const MonthsListContext = createStyledContext<{ size: CalendarSize }>({ size: 'md' });

// ── 5. Styled parts ───────────────────────────────────────────────────────────

/** The grid container — a column of month rows (web `role="grid"`). */
const MonthsListFrame = styled(View, {
  name: 'MonthsList',
  context: MonthsListContext,
  flexDirection: 'column',

  variants: {
    /**
     * Stretch the grid to the full WIDTH of its container — `width: "100%"`.
     * Height stays content-height so a `Sheet` sizes to it; a `flexGrow` here
     * would expand the rows vertically on native.
     */
    fullWidth: {
      true: { width: '100%' }
    }
  } as const
});

/** A single month row (web `role="row"`). */
const MonthsListRow = styled(View, {
  name: 'MonthsListRow',
  context: MonthsListContext,
  flexDirection: 'row',

  variants: {
    /**
     * Row spans the full grid WIDTH. Height stays content-height (the cells'
     * square `minHeight`) — a vertical `flexGrow` would expand each row inside a
     * bounded sheet on native.
     */
    fullWidth: {
      true: { width: '100%' }
    }
  } as const
});

/**
 * A month-cell wrapper (web `role="cell"`). The `PickerControl` leaf inside owns
 * the cell's dimensions/colour/interaction; this wrapper only centres it and,
 * when `fullWidth`, grows to an equal share of the row (a boolean VARIANT — not a
 * per-render `flexGrow` spread — so the optimiser has nothing dynamic to fold).
 */
const MonthsListCell = styled(View, {
  name: 'MonthsListCell',
  context: MonthsListContext,
  alignItems: 'center',
  justifyContent: 'center',

  variants: {
    /** Cell grows to an equal share of the row width. */
    fullWidth: {
      true: { flexGrow: 1, flexBasis: 0 }
    }
  } as const
});

/** Props for the memoized month cell — see {@link MonthsListControlCell}. */
interface MonthsListControlCellProps {
  /** The cell's month, `yyyy-MM-dd`. */
  month: DateStringValue;
  /** Localized month label (resolved once per year in the grid memo). */
  label: string;
  /** Resolved disabled state (bounds ∪ the per-month getter's own flag). */
  disabled: boolean | undefined;

  /** Grid position, used for roving focus. */
  rowIndex: number;
  cellIndex: number;

  /** Roving tabindex for this cell. */
  tabIndex: number;

  size: CalendarSize;
  fullWidth: boolean;
  preventFocus: boolean | undefined;

  /** `styles.cell` props for the wrapper. */
  cellProps: GetProps<typeof MonthsListCell> | undefined;

  /** The raw `getMonthControlProps(month)` result — source of the consumer's handlers. */
  controlProps: Partial<PickerControlProps> | undefined;

  /** `styles.control` merged UNDER `controlProps` — spread onto the leaf. */
  mergedControlProps: Partial<PickerControlProps>;

  /**
   * The `__`-prefixed grid callbacks, pre-stabilised by `MonthsList` with
   * `useCallbackRef` — an unstable identity here would defeat the memo for every
   * cell on every render.
   */
  onControlMouseEnter: (event: unknown, date: DateStringValue) => void;
  onControlClick: (event: ControlPressEvent, date: DateStringValue) => void;
  onControlKeyDown: (
    event: ControlKeyboardEvent,
    payload: { rowIndex: number; cellIndex: number; date: DateStringValue }
  ) => void;
  getControlRef: (
    rowIndex: number,
    cellIndex: number,
    control: { focus: () => void; disabled: boolean | undefined }
  ) => void;
}

/**
 * One month cell, memoized — the `MonthsList` twin of `Month`'s `MonthDayCell`.
 *
 * The grid re-renders for reasons that concern only one or two cells. Most sharply,
 * a RANGE month picker updates `hoveredDate` on every web hover move, which
 * re-renders the whole picker subtree; without a memo boundary that meant all 12
 * `PickerControl` leaves re-rendering (and 12 refs detaching/reattaching, re-running
 * `__getControlRef` and rebuilding the refs matrix) for a change that moved two
 * cells' background colour.
 *
 * The boundary is deliberately an INTERNAL cell, not the public `PickerControl`,
 * whose props contract stays exactly as it was. Tamagui's styled-context and theme
 * reads happen inside the leaf and still propagate past a memo, so cells keep
 * responding to theme/size changes.
 */
const MonthsListControlCell = memo(function MonthsListControlCell({
  month,
  label,
  disabled,
  rowIndex,
  cellIndex,
  tabIndex,
  size,
  fullWidth,
  preventFocus,
  cellProps,
  controlProps,
  mergedControlProps,
  onControlMouseEnter,
  onControlClick,
  onControlKeyDown,
  getControlRef
}: MonthsListControlCellProps) {
  // Tamagui hover handlers are not part of `PickerControl`'s public prop type;
  // attach them via a precisely-typed object spread, the kit's pattern for web-only
  // affordances (see `Month`). Native never fires it.
  const hoverHandlers: { onHoverIn?: (event: unknown) => void } = {
    onHoverIn: event => onControlMouseEnter(event, month)
  };

  return (
    <MonthsListCell role="cell" fullWidth={fullWidth} {...cellProps}>
      <PickerControl
        size={size}
        fullWidth={fullWidth}
        // explicit beats sugar: the `control` slot sits UNDER per-month
        // `getMonthControlProps`, and the consumer's own handlers are
        // preserved (called before ours).
        {...mergedControlProps}
        disabled={disabled}
        ref={node => {
          if (node) {
            getControlRef(rowIndex, cellIndex, {
              focus: () => focusElement(node),
              disabled
            });
          }
        }}
        onKeyDown={(event: ControlKeyboardEvent) => {
          controlProps?.onKeyDown?.(event);
          onControlKeyDown(event, { rowIndex, cellIndex, date: month });
        }}
        onPress={(event: ControlPressEvent) => {
          controlProps?.onPress?.(event);
          onControlClick(event, month);
        }}
        onPressIn={(event: ControlPressInEvent) => {
          withFeedback();
          controlProps?.onPressIn?.(event);
          if (preventFocus && hasPreventDefault(event)) {
            event.preventDefault();
          }
        }}
        tabIndex={tabIndex}
        {...hoverHandlers}
      >
        {controlProps?.children ?? label}
      </PickerControl>
    </MonthsListCell>
  );
}, areCellPropsEqual);

// ── 7. Per-slot `styles` sugar + per-item passthrough ───────────────────────────
// The kit's ONE styling model is props on the parts. `styles` is thin sugar over
// that: a map from named slot → that part's props, resolved through
// `slotStyles().merge` so precedence is fixed in one place —
//   defaults < styles[slot] < explicit xxxProps < inline props.
// Per-month dynamics that a static map can't express (a different prop per date)
// stay on the `getMonthControlProps(date)` callback (Mantine parity); it wins
// over the `control` slot — explicit beats sugar.

/** The named style slots and the styled part each targets. */
export interface MonthsListStyles {
  /** Props for the grid container (`.Frame`). */
  root?: GetProps<typeof MonthsListFrame>;
  /** Props for each month row (`.Row`). */
  row?: GetProps<typeof MonthsListRow>;
  /** Props for each month-cell wrapper (`.Cell`). */
  cell?: GetProps<typeof MonthsListCell>;
  /** Props for each control inside a cell (`.Control` — the `PickerControl` leaf). */
  control?: Partial<PickerControlProps>;
}

const MONTHS_LIST_SLOT_KEYS = [
  'root',
  'row',
  'cell',
  'control'
] as const satisfies readonly (keyof MonthsListStyles)[];

export interface MonthsListSettings extends ControlsGroupSettings {
  /** dayjs format for the month labels. @default 'MMM' */
  monthsListFormat?: string;

  /** Passes props down to each month `PickerControl`, keyed by date (wins over `styles.control`). */
  getMonthControlProps?: (date: DateStringValue) => Partial<PickerControlProps>;

  /** Separate controls/rows with spacing. @default true */
  withCellSpacing?: boolean;

  /** Stretch the list (and its controls) to the full width of its container. @default false */
  fullWidth?: boolean;
}

type MonthsListFrameProps = Omit<
  GetProps<typeof MonthsListFrame>,
  'size' | 'children' | 'fullWidth'
>;

export interface MonthsListProps extends MonthsListFrameProps, MonthsListSettings {
  /** Prevent focus shift when a control is pressed (calls `preventDefault` on press-in). */
  __preventFocus?: boolean;

  /** Whether propagation for the `Escape` key should be stopped (reserved, API parity). */
  __stopPropagation?: boolean;

  /** Year for which the months list is displayed, any date within it (`yyyy-MM-dd`). */
  year: DateStringValue;

  /** Width/font of the controls. @default 'md' */
  size?: CalendarSize;

  /**
   * Per-slot style sugar — props spread onto the matching styled part. Lives on
   * `MonthsListProps` (not the inherited `MonthsListSettings`) so the `styles`
   * shape does not flow up the shared Calendar settings chain and collide with
   * sibling components' own `styles` props.
   */
  styles?: SlotStyles<MonthsListStyles>;
}

/**
 * The month selection grid — a 4×3 grid of `PickerControl`s built from `Box`
 * rows/cells (NEVER HTML `<table>`/`<tr>`/`<td>`), so it renders on web AND
 * native. Each control's selected/in-range/disabled state and roving `tabIndex`
 * (`getMonthInTabOrder`) are derived from `getMonthControlProps` + the min/max
 * bounds; accent comes from the active Tamagui theme. The `__onControl*`
 * callbacks + `__getControlRef` are wired cross-platform so the level groups can
 * drive arrow-key navigation.
 *
 * Per-cell sizing/colour/interaction live in the `PickerControl` leaf (delegated);
 * MonthsList only lays out rows/cells, shares `size` via context, and offers
 * `styles` sugar over its parts plus the per-month `getMonthControlProps`
 * passthrough (explicit beats sugar). Forwards its ref + style props to the grid
 * host (the dates norm).
 *
 * a11y: the root is `role="grid"`, each row `role="row"`, each cell `role="cell"`
 * wrapping a `role="button"` control. (ARIA's `gridcell` would be the ideal child
 * of `role="grid"`, but the cross-platform `Role` type has no `gridcell` member.)
 */
const MonthsListComponent = MonthsListFrame.styleable<MonthsListProps>(
  function MonthsList(props, ref) {
    const {
      year,
      monthsListFormat = 'MMM',
      minDate,
      maxDate,
      getMonthControlProps,
      __getControlRef,
      __onControlKeyDown,
      __onControlClick,
      __onControlMouseEnter,
      __preventFocus,
      __stopPropagation,
      withCellSpacing = true,
      fullWidth = false,
      size = 'md',
      styles,
      ...rest
    } = props;

    // `__stopPropagation` is accepted for Mantine API parity; the cross-platform
    // Escape handling lives at the picker/popover layer, so it is a no-op here
    // (the same reservation `CalendarHeader`/`Month` make for it).
    void __stopPropagation;

    const ctx = useDatesContext();

    const minDateString = minDate == null ? undefined : createDate(minDate).toISODate();
    const maxDateString = maxDate == null ? undefined : createDate(maxDate).toISODate();

    // 7. Typed per-slot accessor (dev-warns unknown keys against the known set).
    const s = slotStyles<MonthsListStyles>(styles, MONTHS_LIST_SLOT_KEYS, 'MonthsList');

    const resolvedLocale = ctx.locale;

    // The 12 cells' grid, bounds and LABELS depend only on the year, the locale,
    // the format and the min/max bounds — never on the selection. They were
    // nonetheless rebuilt on every render: `getMonthsData` costs ~12 dayjs
    // instances plus 12 `format` calls, and each cell then paid another
    // `dayjs(month).locale(…).format(monthsListFormat)` — a parse, a locale clone
    // and a locale-table format — for a label that is fixed for the whole year.
    const monthsInfo = useMemo(() => {
      const grid = getMonthsData(year);
      return {
        grid,
        cells: grid.map(row =>
          row.map(month => ({
            month,
            disabled: isMonthDisabled({ month, minDate: minDateString, maxDate: maxDateString }),
            label: createDate(month).setLocale(resolvedLocale).toFormat(monthsListFormat)
          }))
        )
      };
    }, [year, minDateString, maxDateString, resolvedLocale, monthsListFormat]);

    // ONE per-render cache shared by `getMonthInTabOrder` (which consults the
    // getter twice per month) and the cell loop below (a third time). Not memoized
    // across renders — the getter closes over live selection state.
    const resolveControlProps = memoizeByDate(getMonthControlProps);

    const monthInTabOrder = getMonthInTabOrder({
      months: monthsInfo.grid,
      minDate: minDateString,
      maxDate: maxDateString,
      getMonthControlProps: resolveControlProps
    });

    const cellGap = withCellSpacing ? CELL_SPACING : 0;

    // The cell memo can only bail out if every function prop it receives keeps a
    // stable identity. All four of these arrive freshly built each render (the level
    // group builds them per year, `MonthPicker` per render), so they are wrapped
    // once here rather than being stabilised at each of their many call sites.
    const onControlMouseEnter = useCallbackRef(__onControlMouseEnter);
    const onControlClick = useCallbackRef(__onControlClick);
    const onControlKeyDown = useCallbackRef(__onControlKeyDown);
    const getControlRef = useCallbackRef(__getControlRef);

    const cellSlotProps = s.get('cell');

    const rows = monthsInfo.cells.map((monthsRow, rowIndex) => {
      const cells = monthsRow.map(({ month, disabled: outOfBounds, label }, cellIndex) => {
        const controlProps = resolveControlProps?.(month);
        // Both sides come from `getMonthsData`'s canonical `yyyy-MM-dd`, so the
        // `YYYY-MM` prefix compare is exactly the month-granularity `isSame` it
        // replaces — without a dayjs instance per cell.
        const isMonthInTabOrder =
          monthInTabOrder !== undefined && month.slice(0, 7) === monthInTabOrder.slice(0, 7);
        const disabled = outOfBounds || controlProps?.disabled;

        return (
          <MonthsListControlCell
            key={month}
            month={month}
            label={label}
            disabled={disabled}
            rowIndex={rowIndex}
            cellIndex={cellIndex}
            tabIndex={__preventFocus || !isMonthInTabOrder ? -1 : 0}
            size={size}
            fullWidth={fullWidth}
            preventFocus={__preventFocus}
            cellProps={cellSlotProps}
            controlProps={controlProps}
            mergedControlProps={s.merge('control', controlProps)}
            onControlMouseEnter={onControlMouseEnter}
            onControlClick={onControlClick}
            onControlKeyDown={onControlKeyDown}
            getControlRef={getControlRef}
          />
        );
      });

      return (
        <MonthsListRow
          key={rowIndex}
          role="row"
          columnGap={cellGap}
          fullWidth={fullWidth}
          {...s.get('row')}
        >
          {cells}
        </MonthsListRow>
      );
    });

    return (
      // The grid CONTAINER roles (`grid`/`row`/`cell`) are WEB-ONLY: React-Native's
      // `accessibilityRole` has no `grid`/`row`/`gridcell` member (same root cause
      // as the closed `role="cell"`→`gridcell` question), so there is no native
      // counterpart to add here. Per-cell native announcement is instead carried
      // by each `PickerControl`'s own `accessibilityRole`/`accessibilityState`.
      <MonthsListFrame
        ref={ref}
        role="grid"
        rowGap={cellGap}
        fullWidth={fullWidth}
        {...s.get('root')}
        {...rest}
      >
        {rows}
      </MonthsListFrame>
    );
  }
);

MonthsListComponent.displayName = 'MonthsList';

// ── 14. Public surface ─────────────────────────────────────────────────────────
// `withStaticProperties` exposes the styled parts so consumers can target/extend
// them (`styled(MonthsList.Cell, …)`) — the single source of truth for the grid
// parts.
export const MonthsList = withStaticProperties(MonthsListComponent, {
  Frame: MonthsListFrame,
  Row: MonthsListRow,
  Cell: MonthsListCell
});
